import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Minus, Sparkles } from "lucide-react";

/**
 * MoreThanHpiModal — Worthify vs Traditional vehicle checks (H40l).
 *
 * The previous incarnation of this modal contained the 3-tier pricing UI;
 * that content has moved verbatim to `PricingModal.jsx` (rendered at
 * `/pricing`). The "More Than HPI Check" info button on the homepage
 * still opens THIS file, but now it shows an honest two-column
 * comparison table — Worthify (column 1) vs traditional HPI-style
 * reports (column 2).
 *
 * Editorial rule: do NOT claim competitors lack data they actually
 * provide. We mark MOT history "both yes", and use "varies" rather
 * than "no" for any datapoint that some traditional providers do offer.
 * The honest closing line acknowledges that traditional reports may
 * carry deeper full-provenance/title data — our edge is affordability,
 * AI-driven explanations, the autonomous call, and accessibility.
 *
 * Mobile resilience: portaled into <body>, 100dvh viewport, focus trap,
 * Escape-to-close, scroll lock. Same a11y rigour as the previous modal.
 */

// Compact reusable row renderer — Yes (green check), No (red dash), Yes-with-note.
const Cell = ({ kind, note }) => {
  if (kind === "yes" || kind === "yes-bold") {
    return (
      <div className="flex items-center gap-1.5">
        <Check className="w-4 h-4 flex-shrink-0" style={{ color: "var(--pricing-prem-check)" }} strokeWidth={2.8} />
        <span style={{ fontSize: 12.5, color: kind === "yes-bold" ? "var(--wf-modal-text)" : "var(--wf-modal-text-muted)", fontWeight: kind === "yes-bold" ? 700 : 500 }}>
          {note || "Yes"}
        </span>
      </div>
    );
  }
  if (kind === "no") {
    return (
      <div className="flex items-center gap-1.5">
        <Minus className="w-4 h-4 flex-shrink-0" style={{ color: "var(--verdict-color-high)" }} strokeWidth={2.8} />
        <span style={{ fontSize: 12.5, color: "var(--verdict-color-high)" }}>{note || "Usually no"}</span>
      </div>
    );
  }
  if (kind === "varies") {
    return <span style={{ fontSize: 12.5, color: "var(--verdict-color-medium)", fontWeight: 600 }}>{note || "Varies"}</span>;
  }
  return <span style={{ fontSize: 12.5, color: "var(--wf-modal-text-muted)" }}>{note}</span>;
};

const ROWS = [
  { label: "Typical price",
    worthify: { kind: "yes-bold", note: "£1–£4.99 + optional £2.99 AI call" },
    rivals:   { kind: "varies",   note: "~£20–£30+" } },
  { label: "Plain-English AI verdict & explanations (no jargon)",
    worthify: { kind: "yes" },
    rivals:   { kind: "no" } },
  { label: "Worthify Buy Score (0–100)",
    worthify: { kind: "yes" },
    rivals:   { kind: "no" } },
  { label: "AI negotiation call on your behalf (Pine.ai)",
    worthify: { kind: "yes", note: "Yes — world-first" },
    rivals:   { kind: "no" } },
  { label: "Accessibility-first (deaf, non-verbal, anxious, non-native speakers)",
    worthify: { kind: "yes" },
    rivals:   { kind: "no" } },
  { label: "MOT history & insights",
    worthify: { kind: "yes" },
    rivals:   { kind: "yes" } },
  { label: "Market valuation & price-check",
    worthify: { kind: "yes" },
    rivals:   { kind: "varies" } },
  { label: "Outstanding finance check",
    worthify: { kind: "yes", note: "Yes — live (Full)" },
    rivals:   { kind: "varies" } },
  { label: "Stolen vehicle check",
    worthify: { kind: "yes", note: "Yes — live (Full)" },
    rivals:   { kind: "varies" } },
  { label: "Write-off / insurance (MIAFTR) data",
    worthify: { kind: "yes", note: "Yes — live (Full)" },
    rivals:   { kind: "varies" } },
  { label: "No subscription, pay per vehicle",
    worthify: { kind: "yes" },
    rivals:   { kind: "varies" } },
];

export const MoreThanHpiModal = ({ open, onClose, t }) => {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Scroll lock + focus trap + Escape (same pattern as the previous modal)
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => closeBtnRef.current?.focus(), 80);
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "Tab" && dialogRef.current) {
        const f = dialogRef.current.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  const node = (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(2,6,23,0.78)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            display: "flex", alignItems: "stretch", justifyContent: "center",
            padding: 0,
          }}
          onClick={onClose}
          data-testid="more-than-hpi-modal"
        >
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="comparison-title"
            initial={{ opacity: 0, y: 12, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.99 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              width: "100%",
              maxWidth: 920,
              maxHeight: "100dvh",
              overflow: "auto",
              background: "var(--wf-modal-bg)",
              color: "var(--wf-modal-text-muted)",
              padding: "28px 22px 36px",
              margin: "auto",
              borderRadius: 0,
            }}
          >
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label={t("close") || "Close"}
              data-testid="comparison-close"
              className="absolute top-3 right-3 rounded-full transition-transform hover:scale-105"
              style={{
                width: 40, height: 40,
                background: "var(--wf-modal-card-bg)",
                border: "1px solid rgba(148,163,184,0.20)",
                color: "var(--wf-modal-text-muted)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X className="w-5 h-5" strokeWidth={2.2} />
            </button>

            <div className="flex items-center gap-2 mb-1.5">
              <div
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1"
                style={{ background: "rgba(134,239,172,0.12)", border: "1px solid rgba(134,239,172,0.30)" }}
              >
                <Sparkles className="w-3 h-3" style={{ color: "var(--pricing-prem-check)" }} strokeWidth={2.6} />
                <span style={{ fontSize: 10.5, color: "var(--pricing-prem-check)", fontWeight: 800, letterSpacing: "0.10em", textTransform: "uppercase" }}>
                  Why Worthify
                </span>
              </div>
            </div>
            <h2
              id="comparison-title"
              className="font-black"
              style={{ fontSize: 26, color: "var(--wf-modal-text)", letterSpacing: "-0.4px", lineHeight: 1.15 }}
            >
              More than an HPI-style check
            </h2>
            <p className="mt-1.5" style={{ fontSize: 13.5, color: "var(--wf-modal-text-muted)", lineHeight: 1.5 }}>
              An honest side-by-side with the traditional UK vehicle reports (HPI-style).
              We list what we do, where the field is even, and where we lose.
            </p>

            {/* Table */}
            <div
              className="mt-6 rounded-2xl overflow-hidden"
              style={{ border: "1px solid rgba(148,163,184,0.18)" }}
            >
              {/* Header */}
              <div
                className="grid"
                style={{
                  gridTemplateColumns: "1.4fr 1fr 1fr",
                  background: "var(--wf-modal-card-bg)",
                  padding: "10px 12px",
                  borderBottom: "1px solid rgba(148,163,184,0.18)",
                }}
              >
                <span style={{ fontSize: 11, color: "var(--wf-modal-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Feature
                </span>
                <span style={{ fontSize: 12, color: "var(--pricing-prem-check)", fontWeight: 800 }}>Worthify</span>
                <span style={{ fontSize: 12, color: "var(--wf-modal-text-muted)", fontWeight: 700 }}>
                  Traditional vehicle checks
                  <span style={{ display: "block", fontSize: 10, color: "var(--wf-modal-text-muted)", fontWeight: 500, marginTop: 1 }}>
                    (e.g. HPI-style reports)
                  </span>
                </span>
              </div>
              {ROWS.map((r, i) => (
                <div
                  key={i}
                  className="grid"
                  style={{
                    gridTemplateColumns: "1.4fr 1fr 1fr",
                    padding: "11px 12px",
                    background: i % 2 ? "var(--wf-modal-card-bg)" : "transparent",
                    borderTop: i === 0 ? "none" : "1px solid rgba(148,163,184,0.10)",
                    alignItems: "center",
                  }}
                  data-testid={`comparison-row-${i}`}
                >
                  <span style={{ fontSize: 13, color: "var(--wf-modal-text-muted)", lineHeight: 1.35, paddingRight: 8 }}>
                    {r.label}
                  </span>
                  <Cell kind={r.worthify.kind} note={r.worthify.note} />
                  <Cell kind={r.rivals.kind} note={r.rivals.note} />
                </div>
              ))}
            </div>

            {/* Honest closer */}
            <div
              className="mt-5 rounded-xl p-4"
              style={{
                background: "var(--wf-modal-card-bg)",
                border: "1px solid rgba(148,163,184,0.18)",
              }}
            >
              <p style={{ fontSize: 12.5, color: "var(--wf-modal-text-muted)", lineHeight: 1.55 }}>
                <strong style={{ color: "var(--wf-modal-text)" }}>Where they may win:</strong> traditional reports can carry deeper
                full-provenance / title chain data accumulated over decades of records.
                <br />
                <strong style={{ color: "var(--pricing-prem-check)" }}>Where we win:</strong> affordable plain-English AI analysis, an
                autonomous AI negotiation call that does the difficult conversation for you,
                and an accessibility-first design — for deaf, non-verbal, anxious or
                non-native English buyers.
              </p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href="/pricing"
                onClick={onClose}
                className="font-bold rounded-xl"
                style={{
                  padding: "11px 18px",
                  fontSize: 13.5,
                  background: "linear-gradient(135deg,#10B981 0%,#059669 100%)",
                  color: "var(--wf-modal-text)",
                  minHeight: 44, // WCAG 2.1 AA touch target
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
                data-testid="comparison-see-pricing"
              >
                See full pricing
              </a>
              <a
                href="/why-worthify"
                onClick={onClose}
                className="font-semibold rounded-xl"
                style={{
                  padding: "11px 18px",
                  fontSize: 13.5,
                  background: "rgba(134,239,172,0.08)",
                  color: "var(--pricing-prem-check)",
                  border: "1px solid rgba(134,239,172,0.30)",
                  minHeight: 44,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                }}
                data-testid="comparison-read-why"
              >
                Read the full Why Worthify page
              </a>
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl font-semibold"
                style={{
                  padding: "11px 18px",
                  fontSize: 13.5,
                  background: "var(--wf-modal-card-bg)",
                  color: "var(--wf-modal-text-muted)",
                  border: "1px solid rgba(148,163,184,0.20)",
                  minHeight: 44,
                }}
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(node, document.body);
};

export default MoreThanHpiModal;
